import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import {
  buildClassroomsToiletsLocalGuidance,
  buildClassroomsToiletsMetricsPayload,
} from "@/utils/buildClassroomsToiletsLocalGuidance";

const API = `${getBackendUrl()}/api`;

export function useClassroomsToiletsAiGuidance({
  overview,
  riskSchools,
  equityMetrics,
  hygieneMetrics,
  blockData,
  scopeLabel,
  scopeKey,
}) {
  const [guidance, setGuidance] = useState(null);
  const [provider, setProvider] = useState("local");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const requestIdRef = useRef(0);

  const buildLocal = useCallback(
    () =>
      buildClassroomsToiletsLocalGuidance({
        overview,
        riskSchools,
        equityMetrics,
        hygieneMetrics,
        blockData,
      }),
    [overview, riskSchools, equityMetrics, hygieneMetrics, blockData]
  );

  const fetchGuidance = useCallback(() => {
    if (!overview?.total_schools) {
      setGuidance(null);
      setProvider("local");
      setLoading(false);
      return Promise.resolve();
    }

    const localGuidance = buildLocal();
    const requestId = ++requestIdRef.current;
    const metrics = buildClassroomsToiletsMetricsPayload({
      overview,
      riskSchools,
      equityMetrics,
      hygieneMetrics,
      blockData,
      scopeLabel,
    });

    setLoading(true);
    setError(null);

    return axios
      .post(`${API}/classrooms-toilets/ai-guidance`, { metrics, local_guidance: localGuidance })
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        if (res.data?.guidance) {
          setGuidance(res.data.guidance);
          setProvider(res.data.provider || "local");
          setGeneratedAt(res.data.generated_at || new Date().toISOString());
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setGuidance(localGuidance);
        setProvider("local");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [overview, riskSchools, equityMetrics, hygieneMetrics, blockData, scopeLabel, buildLocal]);

  useEffect(() => {
    fetchGuidance();
  }, [scopeKey, fetchGuidance]);

  return {
    guidance,
    provider,
    loading,
    error,
    generatedAt,
    refresh: fetchGuidance,
    isMistral: provider === "mistral",
  };
}
